import {
  Badge,
  Box,
  Button,
  Group,
  Stack,
  Text,
  Title,
  Divider,
  ActionIcon,
  Tooltip,
  Textarea,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useState } from "react";
import { regenerateAnalysis } from "../monitorsApi";
import LlmProviderLogo from "../../LlmProviders/LlmProviderLogo";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { IconArrowRight, IconRefresh, IconTargetArrow } from "@tabler/icons-react";
import { Link } from "wouter";
import StatsOverview from "./StatsOverview";
import { updateAnalysis } from "./analysisApi";

function RegenerateAnalysisAction({ brandId, analysisId, onReload }) {
  const [loading, setLoading] = useState(false);
  return (
    <Tooltip label="Regenerate Executive Summary" withArrow>
      <ActionIcon
        variant="default"
        loading={loading}
        onClick={async () => {
          setLoading(true);
          try {
            await regenerateAnalysis(brandId, analysisId);
            notifications.show({
              title: "Analysis regenerated",
              message: "Executive summary is being refreshed.",
              color: "green",
            });
            await onReload();
          } catch (error) {
            notifications.show({
              title: "Error",
              message: "Failed to regenerate analysis",
              color: "red",
            });
          } finally {
            setLoading(false);
          }
        }}
      >
        <IconRefresh size={16} />
      </ActionIcon>
    </Tooltip>
  );
}

export default function AnalysisResultCard({
  analysis,
  brandId,
  run,
  reloadAnalysisRuns,
}) {
  const [editMode, setEditMode] = useState(false);
  const [editValue, setEditValue] = useState(analysis.analysis || "");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateAnalysis(analysis.id, { analysis: editValue });
      notifications.show({
        title: "Saved",
        message: "Executive summary updated.",
        color: "green",
      });
      setEditMode(false);
      await reloadAnalysisRuns();
    } catch (error) {
      notifications.show({
        title: "Error",
        message: "Failed to save analysis.",
        color: "red",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack key={analysis.id} mt="md" gap="xs">
      <Group justify="space-between" align="center">
        <Group>
          {analysis.providerId ? (
            <LlmProviderLogo llmProvider={analysis.providerId} size={32} />
          ) : (
            analysis.providerIds?.map((providerId) => (
              <LlmProviderLogo
                llmProvider={providerId}
                size={32}
                key={providerId}
              />
            ))
          )}
        </Group>
        {analysis.providerId ? (
          <Text fw={600} fz="lg">
            {analysis.totalQueries} Queries
          </Text>
        ) : (
          <>
            <Badge>Executive Summary</Badge>
            <RegenerateAnalysisAction
              brandId={brandId}
              analysisId={analysis.id}
              onReload={reloadAnalysisRuns}
            />
          </>
        )}
        {/* For single provider analyses, also show regenerate button */}
        {analysis.providerId && (
          <RegenerateAnalysisAction
            brandId={brandId}
            analysisId={analysis.id}
            onReload={reloadAnalysisRuns}
          />
        )}
      </Group>
      <StatsOverview stats={analysis.stats} />
      <Divider my="sm" />
      {editMode ? (
        <Stack>
          <Textarea
            rows={16}
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            autoFocus
          />
          <Group>
            <Button
              loading={saving}
              onClick={handleSave}
              color="green"
              size="md"
            >
              Save
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setEditMode(false);
                setEditValue(analysis.analysis || "");
              }}
              size="md"
            >
              Cancel
            </Button>
          </Group>
        </Stack>
      ) : (
        <>
          <Group justify="flex-end">
            <Button size="xs" variant="light" onClick={() => setEditMode(true)}>
              Edit
            </Button>
          </Group>
          <Markdown remarkPlugins={[remarkGfm]}>{analysis.analysis}</Markdown>
          {analysis.recommendations && (
            <Box mt="md">
              <Divider my="sm" />
              <Title order={5} mb="xs">
                <Group gap="xs">
                  <IconTargetArrow size={18} />
                  Optimization Recommendations
                </Group>
              </Title>
              <Markdown remarkPlugins={[remarkGfm]}>
                {analysis.recommendations}
              </Markdown>
            </Box>
          )}
        </>
      )}
      <Group justify="flex-end">
        <Button
          variant="outline"
          component={Link}
          to={`/b/${brandId}/runs/${run.id}/prompts`}
          rightSection={<IconArrowRight size={16} />}
        >
          Details
        </Button>
      </Group>
    </Stack>
  );
}
